import { useEffect, useState } from 'react'
import { FormProvider, useFieldArray, useForm, type Control, type FieldArrayPath, type Resolver } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import { PlusCircleIcon } from '@assets/svgs/PlusCircleIcon'
import { emotionTheme } from '@styles/theme'
import { RowBox } from '@components/shared/RowBox'
import { Button } from '@components/shared/Button'
import { Input } from '@components/shared/Input'
import { CalendarIcon } from '@assets/svgs/CalendarIcon'
import { Tooltip } from '@components/shared/Tooltip'
import { InfoIcon } from '@assets/svgs/InfoIcon'
import { initialFormValues } from './form/initialFormValues'
import { distributeBillingModalSchema } from './form/validations'
import { TrashSimple } from '@assets/svgs/TrashSimple'
import { DatePicker } from '@components/shared/DatePicker'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { setInitialValues } from './form/setInitialValues'

import { useClientModal } from '../../context'

import type { DistributeBillingModalProps } from './types'
import type { DistributeBillingModalFormProps } from './form/types'

import * as S from './styles'

export const DistributeBillingModal = ({
  open,
  data,
  loading,
  onClose,
  onConfirm,
  canEdit = true,
  ...rest
}: DistributeBillingModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const methods = useForm<DistributeBillingModalFormProps>({
    defaultValues: initialFormValues,
    resolver: distributeBillingModalSchema as unknown as Resolver<DistributeBillingModalFormProps>
  })

  const { fields, append, remove, replace } = useFieldArray<DistributeBillingModalFormProps>({
    control: methods.control as Control<DistributeBillingModalFormProps>,
    name: 'holidays' as FieldArrayPath<DistributeBillingModalFormProps>
  })

  const { lastDistributionValuesData, isLastDistributionValuesLoading, getLastDistributionValues } = useClientModal()

  const [selectedOption, setSelectedOption] = useState(true)

  const addHoliday = () => append({ date: '' })

  const removeHoliday = (index: number) => remove(index)

  const onSubmit = (formData: DistributeBillingModalFormProps) => {
    if (!canEdit) return

    onConfirm(!selectedOption ? formData : null)
  }

  const handleSetInitialValues = () => {
    setSelectedOption(false)
    methods.reset(setInitialValues(lastDistributionValuesData!))
  }

  const handleResetForm = () => {
    methods.reset(initialFormValues)
    setSelectedOption(true)
    replace([])
  }

  useEffect(() => {
    !!lastDistributionValuesData?.previousMonthBillingDays && handleSetInitialValues()
  }, [lastDistributionValuesData])

  useEffect(() => {
    !!open && !!data && getLastDistributionValues({ filiCodigo: data.filiCodigo, cdClienteNl: data.cdClienteNl })
    !open && handleResetForm()
  }, [open])

  useEffect(() => {
    setSelectedOption(!!selectedOption)
    methods.setValue('distributionType', !!selectedOption)
  }, [selectedOption])

  return (
    <BaseModal2
      open={open}
      width={isMobile ? 'calc(100% - 20px)' : 470}
      heightContainer="auto"
      maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '80%'}
      title="Distribuir faturamento"
      headerIcon={<PlusCircleIcon />}
      onClose={onClose}
      footer={{
        loading,
        cancelButtonText: 'Cancelar',
        cancelButtonMinWidth: 'calc(50% - 8px)',
        confirmButtonText: canEdit ? 'Confirmar' : undefined,
        confirmButtonMinWidth: 'calc(50% - 8px)',
        onConfirm: canEdit ? methods.handleSubmit(onSubmit) : undefined
      }}
      loading={isLastDistributionValuesLoading}
      {...rest}
    >
      <FormProvider {...methods}>
        <S.Title>
          Selecione a opção desejada sobre a distribuição do faturamento do mês anterior na prévia atual:
        </S.Title>

        <S.MenuItem onClick={() => canEdit && setSelectedOption(true)}>
          <S.Radio name="distributionType" checked={!!selectedOption} value={true} disabled={!canEdit} />
          <S.RadioText>Distribuir nos dias do mês atual</S.RadioText>
        </S.MenuItem>

        <S.MenuItem onClick={() => canEdit && setSelectedOption(false)}>
          <S.Radio name="distributionType" checked={!selectedOption} value={false} disabled={!canEdit} />
          <S.RadioText>
            Distribuir nos dias úteis do mês atual
            <Tooltip
              title="A média diária de faturamento do mês anterior será distribuída automaticamente nos dias úteis do mês atual (segunda a sexta-feira). Se necessário, você pode ajustar os valores manualmente."
              placement="top-end"
            >
              <InfoIcon />
            </Tooltip>
          </S.RadioText>
        </S.MenuItem>

        {!selectedOption && (
          <RowBox marginTop={24}>
            <Input
              name="invoiceDays"
              label="Dias de faturamento do mês anterior"
              placeholder="30 - Qtd de dias"
              rightIcon={<CalendarIcon />}
              maskType="onlyNumbers"
              maxLength={2}
              required
              disabled={!canEdit}
            />
            <Input
              name="weekdays"
              label="Segunda a Sexta-feira (%)"
              placeholder="0%"
              maskType="percentage"
              required
              disabled={!canEdit}
            />
            <Input name="saturday" label="Sábado (%)" placeholder="0%" maskType="percentage" disabled={!canEdit} />
            <Input name="sunday" label="Domingo/Feriado (%)" placeholder="0%" maskType="percentage" disabled={!canEdit} />

            <Button
              width="100%"
              style={{ marginTop: 16 }}
              colorType="outlineBlue"
              rightIcon={<PlusCircleIcon />}
              onClick={addHoliday}
              disabled={!canEdit}
            >
              Adicionar feriado
            </Button>

            {fields.map((field, index) => (
              <S.DateBox key={field.id}>
                <DatePicker
                  name={`holidays[${index}].date`}
                  label={`Data do feriado - ${index + 1}`}
                  placeholder="DD/MM/AAAA"
                  disabled={!canEdit}
                />

                <Tooltip width="min-content" title="Remover feriado" placement="top-end">
                  <Button
                    width={24}
                    colorType="outlineBlue"
                    onClick={() => removeHoliday(index)}
                    rightIcon={<TrashSimple />}
                    disabled={!canEdit}
                  />
                </Tooltip>
              </S.DateBox>
            ))}
          </RowBox>
        )}
      </FormProvider>
    </BaseModal2>
  )
}
